import { SegmentedControl, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import React, { useContext, useEffect, useState } from 'react'

import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { YourTheir, YouThey } from '../../utils/driverCopyChange'
const { h4: H4, h5: H5 } = typographyRem

export const DriverMedical = () => {
  const quote = useQuote()
  const {
    state: { selectedDriverIndex, showModal },
  } = useContext(ProviderContext)
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const { updatedDriverDetails, setUpdatedDriverDetails, updatedAdditionalDriver, setUpdatedAdditionalDriver } =
    useDriverPageContext()

  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = !selectedDriverIndex ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]

  const [hasConditions, setHasConditions] = useState<boolean | null | undefined>()
  const [dvlaAware, setDvlaAware] = useState<boolean | null | undefined>()

  useEffect(() => {
    setHasConditions(selectedDriver?.has_medical_conditions)
    setDvlaAware(selectedDriver?.has_informed_dvla_medical_conditions)

    updateValidInputs({
      field: 'hasInformedDvlaMedicalConditions',
      value:
        !selectedDriver?.has_medical_conditions ||
        typeof selectedDriver?.has_informed_dvla_medical_conditions === 'boolean',
      index: selectedDriverIndex,
    })

    updateValidInputs({
      field: 'hasMedicalConditions',
      value: typeof selectedDriver?.has_medical_conditions === 'boolean',
      index: selectedDriverIndex,
    })
  }, [selectedDriverIndex])

  const medicalError =
    showErrorBanner && !validInputs[selectedDriverIndex]?.hasMedicalConditions ? notAnsweredMessage : ''
  const dvlaAwareError =
    showErrorBanner && !validInputs[selectedDriverIndex]?.hasInformedDvlaMedicalConditions ? notAnsweredMessage : ''

  const handleConditions = (value: boolean) => {
    setHasConditions(value)
    setDvlaAware(null)
    DataLayer.customEvent({
      eventName: `has medical conditions ${value ? 'yes' : 'no'}`,
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    !selectedDriverIndex
      ? setUpdatedDriverDetails({
          ...updatedDriverDetails,
          hasMedicalConditions: value,
          hasInformedDvlaMedicalConditions: true,
        })
      : setUpdatedAdditionalDriver({
          ...updatedAdditionalDriver,
          has_medical_conditions: value,
          has_informed_dvla_medical_conditions: true,
        })
  }

  const handleMadeAware = (value: boolean) => {
    setDvlaAware(value)
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, hasInformedDvlaMedicalConditions: value })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, has_informed_dvla_medical_conditions: value })
  }

  useEffect(() => {
    updateValidInputs({
      field: 'hasMedicalConditions',
      value: typeof hasConditions === 'boolean',
      index: selectedDriverIndex,
    })
    updateValidInputs({
      field: 'hasInformedDvlaMedicalConditions',
      value: !!(!hasConditions || dvlaAware),
      index: selectedDriverIndex,
    })
  }, [hasConditions])
  useEffect(() => {
    updateValidInputs({
      field: 'hasInformedDvlaMedicalConditions',
      value: !hasConditions || typeof dvlaAware === 'boolean',
      index: selectedDriverIndex,
    })
  }, [dvlaAware])

  return (
    <>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} medical conditions.`}</H4>
        <div className="generic-wrapper">
          <div className="question-container" id="hasMedicalConditions">
            <div className="question-text-container">
              <H5>{`Do ${YouThey()} have any medical conditions that the DVLA needs to be aware of?`}</H5>
            </div>
            <div className="input-info-container">
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="has-medical-conditions"
                preFilledValue={hasConditions}
                error={hasConditions ? '' : medicalError}
                onClick={value => typeof value === 'boolean' && handleConditions(value)}
                dataCy="has-medical-conditions-toggle"
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
              <ToolTip
                text={
                  <span>
                    {`You need to make your insurer and the DVLA are aware of any health condition that could affect ${YourTheir()}
          driving. If you're not sure what to include here, take a look at the DVLA's list of conditions on
          their website: `}
                    <a href="https://www.gov.uk/driving-medical-conditions" target="_blank" rel="noreferrer">
                      https://www.gov.uk/driving-medical-conditions
                    </a>
                  </span>
                }
                className="tool-tip tip-top-medium"
                tabFocusTrap={showModal ? -1 : 0}
              />
            </div>
          </div>
          {hasConditions && (
            <div className="question-container" id="hasInformedDvlaMedicalConditions">
              <div className="question-text-container">
                <H5>Has the DVLA been made aware of all of these medical conditions?</H5>
              </div>
              <div className="toggle-container">
                <SegmentedControl
                  options={[
                    { label: 'yes', value: true },
                    { label: 'no', value: false },
                  ]}
                  name="has-informed-dvla-medical-conditions"
                  preFilledValue={dvlaAware}
                  error={dvlaAwareError}
                  onClick={value => typeof value === 'boolean' && handleMadeAware(value)}
                  dataCy="has-informed-dvla-medical-conditions-toggle"
                  tabFocusTrap={showModal ? -1 : 0}
                  ariaLiveOnFocus
                />
              </div>
            </div>
          )}
        </div>
      </GenericSectionStyle>
    </>
  )
}
